<template>
  <div id="figures">
    <!-- Export menu backdrop + floating panel (portaled above everything) -->
    <div
      v-if="exportMenuOpen"
      class="fig-export-backdrop"
      @click="exportMenuOpen = false"
    ></div>
    <div
      v-if="exportMenuOpen"
      class="fig-export-menu"
      :style="exportMenuStyle"
    >
      <div class="fig-export-menu-heading">Export figure</div>
      <button class="fig-export-item" @click="exportPng(1)">
        <i class="far fa-image"></i>
        <div>
          <strong>PNG — standard</strong>
          <small>1× resolution</small>
        </div>
      </button>
      <button class="fig-export-item" @click="exportPng(2)">
        <i class="far fa-image"></i>
        <div>
          <strong>PNG — retina (2×)</strong>
          <small>Sharper for slides / web</small>
        </div>
      </button>
      <button class="fig-export-item" @click="exportPng(3)">
        <i class="far fa-image"></i>
        <div>
          <strong>PNG — print (3×)</strong>
          <small>High-DPI for publications</small>
        </div>
      </button>
      <div class="fig-export-sep"></div>
      <button class="fig-export-item" @click="exportCsvData">
        <i class="fas fa-file-csv"></i>
        <div>
          <strong>CSV — chart data</strong>
          <small>Plotted x/y points as CSV</small>
        </div>
      </button>
      <button class="fig-export-item" @click="exportCsvStats">
        <i class="fas fa-calculator"></i>
        <div>
          <strong>CSV — statistics</strong>
          <small>Summary / regression / group stats</small>
        </div>
      </button>
      <button class="fig-export-item" @click="exportJson">
        <i class="fas fa-code"></i>
        <div>
          <strong>JSON — chart config</strong>
          <small>Chart.js config + metadata</small>
        </div>
      </button>
    </div>

    <PluginHeader
      title="Figures Generator"
      subtitle="Build publication-ready figures from any dataset on this farm"
      icon="fas fa-chart-line"
    >
      <template #actions>
        <button
          v-if="generatedChart"
          ref="exportBtn"
          class="btn btn-success btn-sm"
          type="button"
          @click="toggleExportMenu"
        >
          <i class="fas fa-download"></i> Export
          <i class="fas fa-caret-down ml-1"></i>
        </button>
      </template>
    </PluginHeader>

    <div class="container-fluid py-3">
      <div class="row">
        <!-- LEFT: configuration -->
        <div class="col-lg-4">
          <div class="card fig-config">
            <div class="card-body">
              <!-- Dataset picker -->
              <DatasetBrowser
                :selectedDataset="selectedDataset"
                @selectDataset="selectDataset"
              />

              <!-- Dataset loading -->
              <div v-if="loadingDataset" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-primary" />
                <small class="d-block text-muted">Loading dataset…</small>
              </div>

              <!-- Second dataset overlay -->
              <div v-if="selectedDataset && canOverlay" class="mt-3 fig-overlay-block">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <strong class="small">Overlay a second dataset</strong>
                  <button
                    v-if="selectedDataset2"
                    class="btn btn-outline-secondary btn-xs"
                    @click="clearSecondDataset"
                  >
                    <i class="fas fa-times"></i> Clear
                  </button>
                </div>
                <select
                  v-model="secondDatasetId"
                  class="form-control form-control-sm"
                  @change="selectSecondDataset"
                >
                  <option value="">— pick another dataset (optional) —</option>
                  <option
                    v-for="d in compatibleOtherDatasets"
                    :key="d.id"
                    :value="d.id"
                  >{{ d.name }}</option>
                </select>
                <small class="form-text text-muted">
                  Useful for comparing analyses side-by-side on scatter,
                  line, time series, or bar charts.
                </small>
              </div>

              <!-- Quick recipes -->
              <FigureRecipes
                v-if="selectedDataset && selectedDatasetData && !loadingDataset"
                :dataset="datasetForChart"
                class="mt-3"
                @apply="applyRecipe"
              />

              <!-- Figure type grid -->
              <div v-if="selectedDataset && selectedDatasetData" class="mt-3">
                <div class="fig-section-heading">
                  <h6><i class="fas fa-chart-bar"></i> Figure type</h6>
                </div>
                <div class="fig-type-grid">
                  <button
                    v-for="t in figureTypes"
                    :key="t.id"
                    @click="selectFigureType(t)"
                    type="button"
                    class="fig-type-tile"
                    :class="{ active: selectedFigureType && selectedFigureType.id === t.id }"
                    :title="t.description"
                  >
                    <i :class="t.icon"></i>
                    <span>{{ t.name }}</span>
                  </button>
                </div>
              </div>

              <!-- Configurator -->
              <div v-if="selectedDataset && selectedDatasetData && selectedFigureType" class="mt-3">
                <PlotConfigurator
                  :dataset="datasetForChart"
                  :secondDataset="secondDatasetForChart"
                  :figureType="selectedFigureType"
                  :config="plotConfig"
                  @updateConfig="updatePlotConfig"
                  @generateChart="generateChart"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: chart + stats -->
        <div class="col-lg-8">
          <div class="card">
            <div class="card-body">
              <ChartDisplay
                :key="chartKey"
                :chart="generatedChart"
                :loading="generatingChart"
                :error="chartError"
                @chartReady="onChartReady"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { mapState } from 'vuex'
import DatasetBrowser from './components/DatasetBrowser.vue'
import PlotConfigurator from './components/PlotConfigurator.vue'
import ChartDisplay from './components/ChartDisplay.vue'
import FigureRecipes from './components/FigureRecipes.vue'
import PluginHeader from '../_shared/PluginHeader.vue'
import ChartService, { CHART_TYPES, downloadChartPng, downloadChartData, downloadChartStats, downloadChartJson } from './services/ChartService.js'

const fb = require('../../../firebaseConfig.js')

export default {
  name: 'Figures',
  components: { DatasetBrowser, PlotConfigurator, ChartDisplay, FigureRecipes, PluginHeader },
  data () {
    return {
      figureTypes: CHART_TYPES,

      selectedDataset: null,
      selectedDatasetData: null,
      selectedDatasetGeoJson: null,

      secondDatasetId: '',
      selectedDataset2: null,
      selectedDatasetData2: null,
      selectedDatasetGeoJson2: null,

      selectedFigureType: null,
      plotConfig: {},
      generatedChart: null,
      chartInstance: null,
      chartKey: 0,

      loadingDataset: false,
      generatingChart: false,
      chartError: null,

      exportMenuOpen: false,
      exportMenuPos: { top: 60, right: 24 }
    }
  },
  computed: {
    ...mapState(['farm', 'currentUser', 'datasets']),
    datasetForChart () {
      if (!this.selectedDatasetData) return null
      return {
        ...this.selectedDatasetData,
        geoJson: this.selectedDatasetGeoJson
      }
    },
    secondDatasetForChart () {
      if (!this.selectedDatasetData2) return null
      return {
        ...this.selectedDatasetData2,
        geoJson: this.selectedDatasetGeoJson2
      }
    },
    exportMenuStyle () {
      return {
        top: this.exportMenuPos.top + 'px',
        right: this.exportMenuPos.right + 'px'
      }
    },
    canOverlay () {
      if (!this.selectedFigureType) return true
      return ['scatter', 'line', 'timeseries', 'bar'].includes(this.selectedFigureType.id)
    },
    compatibleOtherDatasets () {
      if (!this.datasets || !this.selectedDataset) return []
      return this.datasets
        .filter(d => d.id !== this.selectedDataset.id)
        .filter(d => d.file && (d.file.geoJsonPath || d.file.csvPath))
    }
  },
  mounted () {
    this.$store.dispatch('fetchDatasets', this.farm.id)
    this.$store.dispatch('fetchFolders', this.farm.id)
  },
  methods: {
    // ---- Dataset selection ----
    async selectDataset (dataset) {
      this.selectedDataset = dataset
      this.selectedDatasetData = null
      this.selectedDatasetGeoJson = null
      this.selectedFigureType = null
      this.plotConfig = {}
      this.generatedChart = null
      this.chartError = null
      this.clearSecondDataset()
      await this.loadDatasetData(dataset, 1)
    },

    async selectSecondDataset () {
      if (!this.secondDatasetId) {
        this.clearSecondDataset()
        return
      }
      const ds = (this.datasets || []).find(d => d.id === this.secondDatasetId)
      if (!ds) return
      this.selectedDataset2 = ds
      this.selectedDatasetData2 = null
      this.selectedDatasetGeoJson2 = null
      await this.loadDatasetData(ds, 2)
      // If a chart is already rendered, regenerate with the overlay
      if (this.selectedFigureType && this.plotConfig.xAxis) this.generateChart()
    },

    clearSecondDataset () {
      this.secondDatasetId = ''
      this.selectedDataset2 = null
      this.selectedDatasetData2 = null
      this.selectedDatasetGeoJson2 = null
    },

    async loadDatasetData (dataset, slot = 1) {
      const isSlot1 = slot === 1
      try {
        if (isSlot1) this.loadingDataset = true
        const datasetDoc = await fb.datasetsCollection.doc(dataset.id).get()
        const data = datasetDoc.data()
        data.id = datasetDoc.id

        let geoJson = null
        if (data.file && data.file.geoJsonPath) {
          const firebase = require('firebase/compat/app')
          const ref = firebase.storage().ref(data.file.geoJsonPath)
          const url = await ref.getDownloadURL()
          const resp = await fetch(url)
          geoJson = await resp.json()
        }

        if (isSlot1) {
          this.selectedDatasetData = data
          this.selectedDatasetGeoJson = geoJson
        } else {
          this.selectedDatasetData2 = data
          this.selectedDatasetGeoJson2 = geoJson
        }
      } catch (err) {
        console.error('Failed to load dataset:', err)
        this.chartError = `Could not load dataset: ${err.message}`
      } finally {
        if (isSlot1) this.loadingDataset = false
      }
    },

    // ---- Figure type ----
    selectFigureType (type) {
      this.selectedFigureType = type
      this.plotConfig = this.getDefaultConfig(type)
      this.generatedChart = null
      this.chartError = null
    },

    getDefaultConfig (figureType) {
      // Sensible per-type defaults. PlotConfigurator handles the rest.
      const base = {
        title: '',
        xLabel: '',
        yLabel: '',
        showGrid: true,
        showLegend: true,
        pointColor: '#0B5ED7',
        lineColor: '#0B5ED7',
        barColor: '#0B5ED7'
      }
      switch (figureType.id) {
        case 'scatter':
          return { ...base, xAxis: '', yAxis: '', pointSize: 4, showRegression: true }
        case 'line':
        case 'timeseries':
          return { ...base, xAxis: '', yAxis: '', lineWidth: 2, tension: 0.1, showPoints: false, fillArea: false }
        case 'bar':
          return { ...base, xAxis: '', yAxis: '', aggregation: 'mean' }
        case 'grouped_bar':
          return { ...base, xAxis: '', yFields: [], aggregation: 'mean' }
        case 'histogram':
          return { ...base, xAxis: '', bins: 20, showDensity: true, yLabel: 'Count' }
        case 'box':
          return { ...base, xAxis: '', yAxis: '' }
        case 'density':
          return { ...base, xAxis: '', yLabel: 'Density' }
        case 'correlation':
          return { ...base, columns: [] }
        case 'pie':
          return { ...base, xAxis: '', donut: true }
        default:
          return base
      }
    },

    updatePlotConfig (config) {
      this.plotConfig = { ...this.plotConfig, ...config }
    },

    // ---- Recipes ----
    applyRecipe ({ figureTypeId, config, recipeLabel }) {
      const type = this.figureTypes.find(t => t.id === figureTypeId)
      if (!type) return
      this.selectedFigureType = type
      this.plotConfig = { ...this.getDefaultConfig(type), ...config }
      // Auto-generate the chart so the recipe truly is one-click
      this.$nextTick(() => this.generateChart())
      console.log(`🧪 Applied recipe: ${recipeLabel}`)
    },

    // ---- Chart generation ----
    async generateChart () {
      if (!this.selectedDataset || !this.selectedFigureType) return
      if (!window.Chart && !this.$Chart) {
        this.chartError = 'Chart.js is not loaded on the page.'
        return
      }
      this.generatingChart = true
      this.chartError = null
      try {
        const chart = await ChartService.generateChart(
          this.datasetForChart,
          this.selectedFigureType,
          this.plotConfig,
          this.secondDatasetForChart
        )
        this.generatedChart = chart
        this.chartKey++ // forces ChartDisplay to remount and rebind
      } catch (err) {
        console.error('Chart generation failed:', err)
        this.chartError = err.message || String(err)
      } finally {
        this.generatingChart = false
      }
    },

    onChartReady (chart) {
      this.chartInstance = chart
    },

    // ---- Export ----
    toggleExportMenu () {
      if (this.exportMenuOpen) {
        this.exportMenuOpen = false
        return
      }
      // Measure button position so the floating menu escapes any parent
      // overflow clipping (the card-body has `overflow: hidden` in places).
      this.$nextTick(() => {
        const btn = this.$refs.exportBtn
        if (btn) {
          const rect = btn.getBoundingClientRect()
          this.exportMenuPos = {
            top: rect.bottom + 6,
            right: Math.max(8, window.innerWidth - rect.right)
          }
        }
        this.exportMenuOpen = true
      })
    },
    figureFilename () {
      const title = (this.plotConfig && this.plotConfig.title) || this.selectedFigureType?.name || 'figure'
      return title.toString().replace(/[^\w-]+/g, '_').slice(0, 60)
    },
    exportPng (scale) {
      if (!this.chartInstance) return
      downloadChartPng(this.chartInstance, this.figureFilename(), { scale, backgroundColor: '#ffffff' })
      this.exportMenuOpen = false
    },
    exportCsvData () {
      if (!this.generatedChart) return
      downloadChartData(this.generatedChart, this.figureFilename())
      this.exportMenuOpen = false
    },
    exportCsvStats () {
      if (!this.generatedChart) return
      downloadChartStats(this.generatedChart, this.figureFilename())
      this.exportMenuOpen = false
    },
    exportJson () {
      if (!this.generatedChart) return
      downloadChartJson(this.generatedChart, this.figureFilename())
      this.exportMenuOpen = false
    }
  }
}
</script>

<style scoped>
#figures {
  /* Leave 56px clear at the top for the shared PluginHeader. */
  min-height: 100vh;
  padding-top: 56px;
  background: #f4f7fb;
}

.card {
  border: 1px solid #e5e9f0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}

.fig-toolbar h4 { font-size: 1.15rem; }

/* Section headings in the left config column */
.fig-section-heading h6 {
  font-size: 0.82rem;
  color: #0a4da8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-bottom: 8px;
}

/* Figure type grid */
.fig-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 6px;
}
.fig-type-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 6px;
  border: 1px solid #e5e9f0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.76rem;
  color: #495057;
  transition: all 0.12s;
}
.fig-type-tile i { font-size: 1.15rem; color: #6c757d; }
.fig-type-tile:hover {
  border-color: #0b5ed7;
  background: #f8fbff;
}
.fig-type-tile:hover i { color: #0b5ed7; }
.fig-type-tile.active {
  border-color: #0b5ed7;
  background: #e9f3fe;
  color: #0b5ed7;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(11,94,215,0.15);
}
.fig-type-tile.active i { color: #0b5ed7; }

/* Overlay block */
.fig-overlay-block {
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px dashed #cfe2ff;
  border-radius: 6px;
}
.btn-xs {
  font-size: 0.7rem;
  padding: 1px 6px;
  border-radius: 3px;
}

/* Floating export menu — positioned via JS, portaled above the card */
.fig-export-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: transparent;
}
.fig-export-menu {
  position: fixed;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  padding: 6px;
  min-width: 280px;
  max-height: 85vh;
  overflow-y: auto;
}
.fig-export-menu-heading {
  font-size: 0.72rem;
  color: #6c757d;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 6px 10px 4px;
}
.fig-export-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: transparent;
  border-radius: 5px;
  cursor: pointer;
  text-align: left;
}
.fig-export-item:hover { background: #f1f8ff; }
.fig-export-item i {
  color: #0b5ed7;
  font-size: 1.1rem;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
}
.fig-export-item div { display: flex; flex-direction: column; line-height: 1.2; }
.fig-export-item strong { font-size: 0.82rem; color: #212529; }
.fig-export-item small { font-size: 0.72rem; color: #6c757d; }
.fig-export-sep {
  height: 1px;
  background: #e5e9f0;
  margin: 4px 0;
}
</style>
